// The SaaS billing surfaces' copy decisions — the paywall (PricingGate), the
// Plans page and the app-wide plan-change banner: how a plan and a price are
// named, which explainer names the band, and which refusal codes get their
// own sentence.
//
// The rules underneath live in src/billing/ (planState, planChange,
// paywallCopy, money); only the KEYS live here. Pure, so it is tested without
// rendering.

import { usd } from '../../billing/money';
import { TIER_EXPLAINER, tierExplainer } from '../../billing/planState';
import { mailtoHref } from '../../utils/mailto';

/** What every plan includes, as `billing.features.*` keys. */
export const FEATURE_KEYS = [
  'skus', 'forecasting', 'inventory', 'reorder', 'purchaseOrders',
  'promotions', 'classification', 'safetyStock', 'analytics', 'support',
];

/**
 * The HTTP status of `GET /api/billing/pricing/` for a store that cannot be
 * priced YET (`PricingUnavailable`, billing/views/saas.py billing_pricing).
 */
export const PRICING_NOT_READY_STATUS = 503;

/**
 * The Contact sales link for a band with no listed price. The address is the
 * backend's one configured source (`prices.sales_email`), never a literal.
 */
export function contactSalesMailto(t, salesEmail) {
  return mailtoHref({ to: salesEmail, subject: t('billing.enterpriseMailSubject') });
}

/**
 * A recurring amount: "$29/mo". Money through the shared `usd` rule, so the
 * wire's `str(Decimal)` never prints as "$29.00".
 *
 * @param {Function} t
 * @param {Function} formatNumber  `useFormatters().formatNumber`
 * @param {string} interval        'monthly' | 'annual'
 * @param {string|number} price
 */
export function priceLabel(t, formatNumber, interval, price) {
  const per = interval === 'annual'
    ? t('billing.planChange.perYear')
    : t('billing.planChange.perMonth');
  return `${usd(price, formatNumber)}/${per}`;
}

/**
 * How a plan is named wherever one is named: "Core, $290/yr". The name
 * stands alone when the payload carries no price, never trailing an empty
 * amount.
 */
export function planLabel(t, formatNumber, plan, interval, price) {
  if (price == null || price === '') return plan;
  return t('billing.planChange.planLabel', {
    plan, price: priceLabel(t, formatNumber, interval, price),
  });
}

const TIER_EXPLAINER_KEYS = {
  [TIER_EXPLAINER.QUOTED]: 'billing.plans.tierExplainerQuoted',
  [TIER_EXPLAINER.REVENUE]: 'billing.plans.tierExplainer',
  [TIER_EXPLAINER.UNDER_A_YEAR]: 'billing.plans.lessThan12Months',
};

/** The sentence under the Billing settings card's heading that says how the band was set. */
export function tierExplainerKey(pricing) {
  return TIER_EXPLAINER_KEYS[tierExplainer(pricing)];
}

// Refusal codes a merchant can act on get their own sentence; every other
// code (a defect on our side of the wire, or a race the page cannot
// explain better) renders the caller's generic failure.
//
// Subscribe: billing.services.subscribe + the view's store guards.
export const SUBSCRIBE_ERROR_KEYS = {
  contact_sales: 'billing.errors.contactSales',
  store_not_resolved: 'billing.errors.storeNotResolved',
  pricing_not_ready: 'billing.errors.pricingNotReady',
  // A development store past its evaluation, clicking Subscribe from the
  // ladder a rowless store still sees (the status payload never asks
  // Shopify who the store is; the click does).
  dev_store_evaluation_closed: 'billing.errors.devStoreEvaluationClosed',
};

// Switch: billing.services.switching.switch_subscription.
export const SWITCH_ERROR_KEYS = {
  contact_sales: 'billing.errors.contactSales',
  pricing_not_ready: 'billing.errors.pricingNotReady',
};
